import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  MessageSquare,
  Search,
  Trash2,
  X,
  Mail,
  Phone,
  User,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminMessages = () => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API}/api/admin/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [token]);

  const updateMessageStatus = async (id, status) => {
    try {
      const response = await fetch(`${API}/api/admin/messages/${id}/status?status=${status}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Status updated');
        fetchMessages();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/messages/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Message deleted');
        setSelectedMessage(null);
        fetchMessages();
      }
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      unread: 'bg-blue-100 text-blue-700',
      read: 'bg-slate-100 text-slate-700',
      replied: 'bg-green-100 text-green-700',
      spam: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  const filteredMessages = messages.filter(msg => {
    const matchesSearch = 
      msg.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || msg.status === filter;
    
    return matchesSearch && matchesFilter;
  });

  const statusCounts = {
    all: messages.length,
    unread: messages.filter(m => m.status === 'unread').length,
    read: messages.filter(m => m.status === 'read').length,
    replied: messages.filter(m => m.status === 'replied').length,
    spam: messages.filter(m => m.status === 'spam').length,
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
              <MessageSquare className="w-7 h-7 text-primary" />
              Messages
            </h1>
            <p className="text-slate-600">View and manage contact form messages</p>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(statusCounts).map(([status, count]) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border'
              }`}
              data-testid={`filter-${status}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
            </button>
          ))}
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="message-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Messages List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading messages...</div>
            ) : filteredMessages.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No messages found</div>
            ) : (
              <div className="divide-y">
                {filteredMessages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${message.status === 'unread' ? 'bg-blue-50/50' : ''}`}
                    onClick={() => {
                      setSelectedMessage(message);
                      if (message.status === 'unread') {
                        updateMessageStatus(message.id, 'read');
                      }
                    }}
                    data-testid={`message-${message.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">{message.full_name}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(message.status)}`}>
                              {message.status}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-slate-700 mt-1">{message.subject}</p>
                          <p className="text-sm text-slate-500 truncate">{message.message}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {message.email}
                            </span>
                            {message.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {message.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-slate-400">
                        {formatDate(message.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Message Detail Modal */}
        {selectedMessage && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Message Details
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedMessage(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b">
                  <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{selectedMessage.full_name}</h3>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {selectedMessage.email}
                      </span>
                      {selectedMessage.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {selectedMessage.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-500">Subject</Label>
                  <p className="font-medium text-slate-800">{selectedMessage.subject}</p>
                </div>

                <div>
                  <Label className="text-slate-500">Message</Label>
                  <div className="bg-slate-50 p-4 rounded-lg mt-1">
                    <p className="text-slate-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-500">Received</Label>
                  <p className="text-sm text-slate-600">{formatDate(selectedMessage.created_at)}</p>
                </div>

                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => updateMessageStatus(selectedMessage.id, 'read')}
                    className="text-slate-600"
                    data-testid="mark-read-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Mark as Read
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateMessageStatus(selectedMessage.id, 'replied')}
                    className="text-green-600"
                    data-testid="mark-replied-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Replied
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateMessageStatus(selectedMessage.id, 'spam')}
                    className="text-orange-600"
                    data-testid="mark-spam-btn"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Mark as Spam
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDelete(selectedMessage.id)}
                    className="text-red-600 hover:bg-red-50"
                    data-testid="delete-message-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
